import { Star, Timer } from 'lucide-react'
import { Email } from '../../types/email'
import { getAccountById } from '../../data/mockEmails'

interface EmailItemProps {
  email: Email
  isSelected: boolean
  onClick: () => void
  autoDeleteDays?: number
}

function formatDate(date: Date): string {
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  const days = Math.floor(diff / (1000 * 60 * 60 * 24))

  if (days === 0) {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  } else if (days === 1) {
    return 'Yesterday'
  } else if (days < 7) {
    return date.toLocaleDateString('en-US', { weekday: 'short' })
  } else {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    })
  }
}

export default function EmailItem({ email, isSelected, onClick, autoDeleteDays }: EmailItemProps) {
  const account = getAccountById(email.accountId)

  return (
    <div
      onClick={onClick}
      className={`flex items-start gap-3 p-4 cursor-pointer border-b border-gray-100 transition-colors ${
        isSelected
          ? 'bg-primary-50 border-l-2 border-l-primary-600'
          : 'hover:bg-gray-50'
      } ${!email.isRead ? 'bg-white' : 'bg-gray-50/50'}`}
    >
      {/* Account Indicator */}
      <div
        className="w-1 h-12 rounded-full flex-shrink-0 mt-1"
        style={{ backgroundColor: account?.color || '#6366f1' }}
        title={account?.email}
      />

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-2 min-w-0">
            <span
              className={`truncate ${
                !email.isRead ? 'font-semibold text-gray-900' : 'text-gray-700'
              }`}
            >
              {email.from.name}
            </span>
            {/* Auto-delete timer indicator */}
            {autoDeleteDays && autoDeleteDays > 0 && (
              <span
                className="flex items-center gap-0.5 px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] rounded-full flex-shrink-0"
                title={`Auto-deletes after ${autoDeleteDays} days`}
              >
                <Timer size={10} className="animate-pulse" />
                {autoDeleteDays}d
              </span>
            )}
          </div>
          <span className="text-xs text-gray-500 flex-shrink-0">
            {formatDate(email.date)}
          </span>
        </div>

        <div
          className={`truncate mb-1 ${
            !email.isRead ? 'font-medium text-gray-900' : 'text-gray-600'
          }`}
        >
          {email.subject}
        </div>

        <div className="text-sm text-gray-500 truncate">{email.preview}</div>
      </div>

      {/* Star */}
      {email.isStarred && (
        <Star
          size={16}
          className="flex-shrink-0 text-yellow-400 fill-yellow-400 mt-1"
        />
      )}
    </div>
  )
}
